/**
 * Interned row identifier — generic-tagged for variant narrowing.
 *
 * `RowId.of()` caches instances so the same canonical string always
 * returns the **same object reference** — `===` works between RowId
 * instances and `Map<RowId, V>` works natively.
 *
 * Canonical forms:
 *   UUID     → `r_<32hex>`
 *   r_hex    → `r_<32hex>`  (identity)
 *   :key     → `:key`       (user lookup key)
 *   :~key    → `:~key`      (reserved key)
 *   virtual  → `virtual(r_<32hex>|<preset>)`
 *   system   → `system(<name>)` or `system(<name>|<param>)`
 *   template → `template(name)`
 *   pending  → `pending(uuid)`
 *
 * **Generic-tagged**: `RowId<"user">` / `RowId<"lookup">` / etc. via
 * the `__rowIdVariant` phantom field. `isUser()` / `asUser()` etc.
 * narrow the type. The pre-port tento/tento-lona-js/sheets class was simpler
 * (non-generic, public `value` / `variant` fields) but didn't
 * support type-level variant discrimination — adopting the
 * generic shape unblocks cross-package consumer migration: identity
 * types need one canonical implementation, because parallel classes
 * with different private fields are nominally incompatible.
 *
 * Public `value` / `variant` getters preserve the rows-side API;
 * the internal `canonicalForm` / `variantKind` fields stay private to
 * keep the structural type of two `RowId` instances nominally
 * identical (TypeScript's private-field equality requires same
 * class + same names).
 */

import type { Option } from "../core/option.ts";
import { Uuid } from "@tento-core/uuid";

import type { RowLocalId } from "./row-local-id.ts";
import { elementAt } from "../core/checked-index.ts";
import { WeakIntern } from "./weak-intern.ts";

const HEX32_RE = /^[0-9a-f]{32}$/i;

/**
 * Deterministic 128-bit FNV-1a hash (4 parallel 32-bit runs) → 32 hex chars.
 * Same seed → same hex. Shared by `RowId.deterministic` (row ids) and the
 * sheet-definition hydrator's sheet-id derivation, so both stay in lockstep
 * without a cross-package `SheetId` import.
 */
export function fnvHex(seed: string): string {
  const FNV_OFFSET = 0x811c9dc5;
  const FNV_PRIME = 0x01000193;
  let hex = "";
  for (let pass = 0; pass < 4; pass++) {
    let h = FNV_OFFSET >>> 0;
    h ^= pass;
    h = Math.imul(h, FNV_PRIME) >>> 0;
    for (let i = 0; i < seed.length; i++) {
      h ^= seed.charCodeAt(i);
      h = Math.imul(h, FNV_PRIME) >>> 0;
    }
    hex += h.toString(16).padStart(8, "0");
  }
  return hex;
}

/**
 * A row's server id, parsed and interned; its variant tag says which kind of
 * row it names.
 */
export class RowId<V extends RowId.VariantTag = RowId.VariantTag> {
  declare readonly __rowIdVariant: V;

  /** Interned weakly: one instance per spelling while held (`WeakIntern`). */
  static readonly #cache = new WeakIntern<RowId>();

  readonly #canonicalForm: string;
  readonly #variantKind: RowId.Variant;

  private constructor(canonical: string, variant: RowId.Variant) {
    this.#canonicalForm = canonical;
    this.#variantKind = variant;
    // Interned and shared by every reader (`RowId.of`), so it is frozen.
    Object.freeze(this);
  }

  // ── Factory methods ──────────────────────────────────────────────

  static of(raw: `pending(${string})`): RowId.Pending;
  static of(raw: `system(${string})`): RowId.System;
  static of(raw: `virtual(${string})`): RowId.Virtual;
  static of(raw: `r_${string}~${string}`): RowId.Virtual;
  static of(raw: `:~${string}`): RowId.Reserved;
  static of(raw: `:${string}`): RowId.Lookup;
  static of(raw: `r_${string}`): RowId.User;
  static of(raw: string): RowId;
  static of(raw: string): RowId {
    const cached = RowId.#cache.get(raw);
    if (cached) return cached;

    const { canonical, variant } = RowId.#canonicalize(raw);

    const cachedCanonical = RowId.#cache.get(canonical);
    if (cachedCanonical) {
      if (raw !== canonical) RowId.#cache.set(raw, cachedCanonical);
      return cachedCanonical;
    }

    const id = new RowId(canonical, variant);
    RowId.#cache.set(canonical, id);
    if (raw !== canonical) RowId.#cache.set(raw, id);
    return id;
  }

  static fromUuid(uuid: string): RowId.User {
    return RowId.of(uuid) as RowId.User;
  }

  /**
   * Deterministic user-variant id from a stable seed (FNV-1a → `r_<32hex>`).
   * Same seed → same id, so reopening a definition (catalog builtin or
   * template) yields identical row ids — stable selection / deep-link. The id
   * is PROVISIONAL: on promote the server mints/reuses canonical row ids and
   * the client remaps (see the sheet-definition identity invariant). Replaces
   * the ad-hoc `fakeUserRowId` char-code padding with collision-resistant FNV.
   */
  static deterministic(seed: string): RowId.User {
    return RowId.of(`r_${fnvHex(seed)}`) as RowId.User;
  }

  static lookup(key: string): RowId.Lookup {
    const canonical = key.startsWith(":") ? key : `:${key}`;
    return RowId.of(canonical) as RowId.Lookup;
  }

  static reserved(key: string): RowId.Reserved {
    const canonical = key.startsWith(":~") ? key : `:~${key}`;
    return RowId.of(canonical) as RowId.Reserved;
  }

  static virtual(parentId: RowId, preset: string): RowId.Virtual {
    const parentHex = parentId.toHex();
    if (!parentHex) {
      throw new Error(
        `RowId.virtual: parentId must be a user row, got ${parentId}`,
      );
    }
    return RowId.of(
      `virtual(r_${parentHex}|${preset})`,
    ) as RowId.Virtual;
  }

  static system(name: string, param: Option<string>): RowId.System {
    const s = param != null ? `system(${name}|${param})` : `system(${name})`;
    return RowId.of(s) as RowId.System;
  }

  static template(name: string): RowId.Template {
    return RowId.of(`template(${name})`) as RowId.Template;
  }

  static mock(id: string): RowId.Template {
    return RowId.template(id);
  }

  static pending(uuid: Option<string>): RowId.Pending {
    const id = uuid ?? crypto.randomUUID().replace(/-/g, "");
    return RowId.of(`pending(${id})`) as RowId.Pending;
  }

  /** Convert a `RowLocalId` back to a `RowId`. */
  static fromLocalId(id: RowLocalId): RowId {
    return RowId.of(id.toString());
  }

  // ── Public getters (rows-side API preservation) ─────────────────

  /** Canonical wire-format string. Same as `toString()`. */
  get value(): string {
    return this.#canonicalForm;
  }

  /**
   * Variant tag string — `"user"` / `"lookup"` / etc. Use
   * `isUser()` / `isLookup()` / etc. for type-narrowing checks;
   * this getter is the back-compat path for code that read
   * `id.variant` directly (tento/tento-lona-js/sheets pre-port API).
   */
  get variant(): RowId.VariantTag {
    return this.#variantKind.tag;
  }

  // ── Inspectors ───────────────────────────────────────────────────

  isVirtual(): this is RowId.Virtual {
    return this.#variantKind.tag === "virtual";
  }

  isLookup(): this is RowId.Lookup {
    return this.#variantKind.tag === "lookup";
  }

  isReserved(): this is RowId.Reserved {
    return this.#variantKind.tag === "reserved";
  }

  isUser(): this is RowId.User {
    return this.#variantKind.tag === "user";
  }

  isSystem(): this is RowId.System {
    return this.#variantKind.tag === "system";
  }

  isTemplate(): this is RowId.Template {
    return this.#variantKind.tag === "template";
  }

  isPending(): this is RowId.Pending {
    return this.#variantKind.tag === "pending";
  }

  asServerBacked(): Option<RowId<RowId.ServerBacked>> {
    if (this.#variantKind.tag === "user") {
      return this as RowId<RowId.ServerBacked>;
    }
    return null;
  }

  assertServerBacked(): RowId<RowId.ServerBacked> {
    if (this.#variantKind.tag === "user") {
      return this as RowId<RowId.ServerBacked>;
    }
    throw new Error(
      `Invariant: expected server-backed RowId (user), got ${this.#variantKind.tag}: ${this.#canonicalForm}`,
    );
  }

  // ── Extractors ───────────────────────────────────────────────────

  asVirtual(): Option<RowId.VirtualInfo> {
    if (this.#variantKind.tag !== "virtual") return null;
    return {
      parentId: RowId.of(`r_${this.#variantKind.parentHex}`) as RowId.User,
      preset: this.#variantKind.preset,
    };
  }

  asLookup(): Option<string> {
    if (this.#variantKind.tag === "lookup") return this.#variantKind.key;
    if (this.#variantKind.tag === "reserved") return this.#variantKind.key;
    return null;
  }

  asSystem(): Option<RowId.SystemInfo> {
    if (this.#variantKind.tag !== "system") return null;
    return { name: this.#variantKind.name, param: this.#variantKind.param };
  }

  asTemplate(): Option<RowId.TemplateInfo> {
    if (this.#variantKind.tag !== "template") return null;
    return { name: this.#variantKind.name };
  }

  asPending(): Option<RowId.PendingInfo> {
    if (this.#variantKind.tag !== "pending") return null;
    return { uuid: this.#variantKind.uuid };
  }

  /** Restore the UUID with dashes (8-4-4-4-12). Returns null for non-user rows. */
  toUuid(): Option<string> {
    let hex: Option<string> = null;
    if (this.#variantKind.tag === "user") hex = this.#variantKind.hex;
    if (!hex) return null;
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  /** Raw 32-char hex (no prefix). Returns null for non-user rows. */
  toHex(): Option<string> {
    if (this.#variantKind.tag === "user") return this.#variantKind.hex;
    return null;
  }

  // ── Interop ──────────────────────────────────────────────────────

  toString(): string {
    return this.#canonicalForm;
  }

  toJSON(): string {
    return this.#canonicalForm;
  }

  // ── Internals ────────────────────────────────────────────────────

  static #canonicalize(raw: string): {
    canonical: string;
    variant: RowId.Variant;
  } {
    // 1. Reserved key — `:~calendar`, `:~garmin:sleep`
    if (raw.startsWith(":~")) {
      return {
        canonical: raw,
        variant: { tag: "reserved", key: raw.slice(2) },
      };
    }

    // 2. Lookup key — `:revenue`, `:my-row`
    if (raw.startsWith(":")) {
      return {
        canonical: raw,
        variant: { tag: "lookup", key: raw.slice(1) },
      };
    }

    // 3. Virtual row — `virtual(r_<hex>|<preset>)`
    if (raw.startsWith("virtual(") && raw.endsWith(")")) {
      const inner = raw.slice(8, -1);
      const pipeIdx = inner.indexOf("|");
      if (pipeIdx !== -1 && inner.startsWith("r_")) {
        const parentHex = inner.slice(2, pipeIdx).toLowerCase();
        const preset = inner.slice(pipeIdx + 1);
        if (HEX32_RE.test(parentHex) && preset.length > 0) {
          return {
            canonical: `virtual(r_${parentHex}|${preset})`,
            variant: { tag: "virtual", parentHex, preset },
          };
        }
      }
    }

    // 3b. Legacy virtual — `[r_hex,virt,preset::name]`
    if (raw.startsWith("[") && raw.endsWith("]")) {
      const match = raw.match(
        /^\[r_([0-9a-f]{32}),virt,preset::([^\]]+)\]$/i,
      );
      if (match) {
        const parentHex = elementAt(match, 1, "RowId.canonicalize: legacy virtual parentHex").toLowerCase();
        const preset = elementAt(match, 2, "RowId.canonicalize: legacy virtual preset");
        return {
          canonical: `virtual(r_${parentHex}|${preset})`,
          variant: { tag: "virtual", parentHex, preset },
        };
      }
    }

    // 3c. Legacy virtual — `r_<hex>~<preset>` (canonicalize to new format)
    if (raw.startsWith("r_") && raw.includes("~")) {
      const body = raw.slice(2);
      const tildeIdx = body.indexOf("~");
      const parentHex = body.slice(0, tildeIdx).toLowerCase();
      const preset = body.slice(tildeIdx + 1);
      if (HEX32_RE.test(parentHex) && preset.length > 0) {
        return {
          canonical: `virtual(r_${parentHex}|${preset})`,
          variant: { tag: "virtual", parentHex, preset },
        };
      }
    }

    // 4. System row — `system(header)`, `system(all-day|<id>)`
    if (raw.startsWith("system(") && raw.endsWith(")")) {
      const inner = raw.slice(7, -1);
      const pipeIdx = inner.indexOf("|");
      if (pipeIdx !== -1) {
        const name = inner.slice(0, pipeIdx);
        const param = inner.slice(pipeIdx + 1);
        return {
          canonical: `system(${name}|${param})`,
          variant: { tag: "system", name, param },
        };
      }
      const colonIdx = inner.indexOf(":");
      if (colonIdx !== -1) {
        const name = inner.slice(0, colonIdx);
        const param = inner.slice(colonIdx + 1);
        return {
          canonical: `system(${name}|${param})`,
          variant: { tag: "system", name, param },
        };
      }
      return {
        canonical: raw,
        variant: { tag: "system", name: inner, param: null },
      };
    }

    // 4b. Template row — `template(food_row)`
    if (raw.startsWith("template(") && raw.endsWith(")")) {
      const name = raw.slice(9, -1);
      return { canonical: raw, variant: { tag: "template", name } };
    }

    // 4c. Pending row — `pending(uuid)`
    if (raw.startsWith("pending(") && raw.endsWith(")")) {
      const uuid = raw.slice(8, -1);
      return { canonical: raw, variant: { tag: "pending", uuid } };
    }

    // 5. Plain r_hex (user row)
    if (raw.startsWith("r_")) {
      const hex = raw.slice(2).toLowerCase();
      if (HEX32_RE.test(hex)) {
        return {
          canonical: `r_${hex}`,
          variant: { tag: "user", hex },
        };
      }
    }

    // 6. Raw UUID with dashes
    if (Uuid.is(raw)) {
      const hex = raw.replace(/-/g, "").toLowerCase();
      return {
        canonical: `r_${hex}`,
        variant: { tag: "user", hex },
      };
    }

    // 7. Bare 32-char hex (no prefix)
    if (HEX32_RE.test(raw)) {
      const hex = raw.toLowerCase();
      return {
        canonical: `r_${hex}`,
        variant: { tag: "user", hex },
      };
    }

    // Fallback: treat as lookup key. Pre-port tento/tento-lona-js/sheets
    // canonicalized by prefixing `:`; the lona-js port behaviour
    // (kept verbatim here) leaves the raw input as canonical.
    return {
      canonical: raw,
      variant: { tag: "lookup", key: raw },
    };
  }

}

/**
 * Types that go with `RowId`: `VariantTag`, `ServerBacked`, `User`, `Lookup`,
 * `Reserved`, `Virtual`, and 8 more.
 */
export namespace RowId {
  export type VariantTag =
    | "user"
    | "lookup"
    | "reserved"
    | "virtual"
    | "system"
    | "template"
    | "pending";

  export type ServerBacked = "user";

  export type User = RowId<"user">;
  export type Lookup = RowId<"lookup">;
  export type Reserved = RowId<"reserved">;
  export type Virtual = RowId<"virtual">;
  export type System = RowId<"system">;
  export type Template = RowId<"template">;
  export type Pending = RowId<"pending">;

  export type VirtualInfo = { parentId: RowId.User; preset: string };
  export type SystemInfo = { name: string; param: Option<string> };
  export type TemplateInfo = { name: string };
  export type PendingInfo = { uuid: string };

  export type Variant =
    | { tag: "user"; hex: string }
    | { tag: "lookup"; key: string }
    | { tag: "reserved"; key: string }
    | { tag: "virtual"; parentHex: string; preset: string }
    | { tag: "system"; name: string; param: Option<string> }
    | { tag: "template"; name: string }
    | { tag: "pending"; uuid: string };

  export namespace System {
    /** Parse the linked calendar row ID from a timeline system row. */
    export function parseTimelineLink(id: RowId.System): Option<RowId> {
      const info = id.asSystem();
      if (!info) return null;
      if ((info.name === "timeline" || info.name === "all-day") && info.param) {
        return RowId.of(info.param);
      }
      return null;
    }
  }
}
